import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import EmptyRoom from './EmptyRoom';
import {
  getHeroImageForRoomType,
  getImageForRoomType,
  getRapFireImageForRoomType,
  getRoomThemeKey,
  getRoomTypeFromGame,
} from './base/HousieRoomTypeCard';
import AppFooter from './base/AppFooter';
import RoomListAdsCarousel from './RoomListAdsCarousel';
import CreateRoomNameModal from '../modals/CreateRoomNameModal';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { resolveWaitingMediaUrl } from './RoomDetailLobby';
import {
  rapFireCreateRoom,
  rapFireSelectRoom,
  rapFireUserRoom,
  tambolaCreateRoom,
  tambolaSelectRoom,
  tambolaUserRoom,
} from '../network/network_callbacks';
import { ROUTE_ENUM } from '../routes/RoutesConstant';
import { logger } from '../utils/logger';
import { getGameHeroLottie } from '../constants/LottieConstants';
import './HostRoomScreen.css';

const HOUSIE_FALLBACKS = {
  family: {
    title: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_SUBTITLE_2,
  },
  kitty: {
    title: TEXT_LABELS.RAP_HOUSIE_KITTY_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_HOUSIE_KITTY_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_HOUSIE_KITTY_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_HOUSIE_KITTY_ROOM_SUBTITLE_2,
  },
  corporate: {
    title: TEXT_LABELS.RAP_HOUSIE_CORPORATE_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_HOUSIE_CORPORATE_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_HOUSIE_CORPORATE_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_HOUSIE_CORPORATE_ROOM_SUBTITLE_2,
  },
};

const FIRE_FALLBACKS = {
  family: {
    title: TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_SUBTITLE_2,
  },
  kitty: {
    title: TEXT_LABELS.RAP_FIRE_KITTY_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_FIRE_KITTY_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_FIRE_KITTY_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_FIRE_KITTY_ROOM_SUBTITLE_2,
  },
  corporate: {
    title: TEXT_LABELS.RAP_FIRE_CORPORATE_ROOM_TITLE,
    subtitle: TEXT_LABELS.RAP_FIRE_CORPORATE_ROOM_SUBTITLE_1,
    limit: TEXT_LABELS.RAP_FIRE_CORPORATE_ROOM_LIMIT,
    tagline: TEXT_LABELS.RAP_FIRE_CORPORATE_ROOM_SUBTITLE_2,
  },
};

const roomsFromFallbacks = (fallbacks) => [
  { heading: { name: fallbacks.family.title } },
  { heading: { name: fallbacks.kitty.title } },
  { heading: { name: fallbacks.corporate.title } },
];

const resolveRoomCopy = (room, fallbacks) => {
  const key = getRoomThemeKey(room?.heading?.name || '');
  const fallback = fallbacks[key] || fallbacks.family;
  return {
    title: room?.heading?.name || fallback.title,
    subtitle: room?.desc || fallback.subtitle,
    limit: room?.descLimit || fallback.limit,
    tagline: room?.descBottom || fallback.tagline,
  };
};

const HostRoomScreen = ({ gameId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isRapFire = gameId === 'rap_fire';
  const fallbacks = isRapFire ? FIRE_FALLBACKS : HOUSIE_FALLBACKS;
  const fallbackRooms = useMemo(() => roomsFromFallbacks(fallbacks), [fallbacks]);
  const selectRoomPath = isRapFire
    ? ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath
    : ROUTE_ENUM.RAP_HOUSIE_ROOM_TYPE.routePath;
  const lobbyPath = isRapFire
    ? ROUTE_ENUM.RAP_FIRE_ROOM_CODE_SHARE_START_GAME.routePath
    : ROUTE_ENUM.RAP_HOUSIE_ROOM_CODE_SHARE_START_GAME.routePath;

  const {
    isJoin,
    availableRooms: incomingRooms,
    openCreateRoomModal,
    pendingRoomType,
    pendingDisplayRoomData,
  } = location.state || {};

  const [roomTypes, setRoomTypes] = useState(fallbackRooms);
  const [availableRooms, setAvailableRooms] = useState(incomingRooms || []);
  const [nextGame, setNextGame] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(Boolean(openCreateRoomModal));
  const [selectedRoomType, setSelectedRoomType] = useState(pendingRoomType || '');
  const [selectedDisplayRoomData, setSelectedDisplayRoomData] = useState(
    pendingDisplayRoomData || null
  );
  const [loading, setLoading] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduceMotion(event.matches);
    setReduceMotion(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const copy = useMemo(() => {
    if (isRapFire) {
      return {
        pageTitle: TEXT_LABELS.RAP_HOUSIE_HOST_PAGE_TITLE,
        backLabel: TEXT_LABELS.RAP_HOUSIE_BACK_TO_GAMES,
        heroTitle: TEXT_LABELS.RAP_FIRE_HOST_HERO_TITLE,
        heroSubtitle: TEXT_LABELS.RAP_FIRE_HOST_HERO_SUBTITLE,
        inviteLine: TEXT_LABELS.RAP_FIRE_HOST_INVITE_LINE,
        heroImage: IMAGE_CONSTANTS.V2_RAP_FIRE_DASHBOARD_HERO,
        heroBackground: IMAGE_CONSTANTS.V2_REVAMPED_RAPFIRE_HERO_BG,
        createLabel: TEXT_LABELS.RAP_FIRE_ROOM_CREATE_BUTTON_LABEL,
        sidebarTitle: TEXT_LABELS.RAP_FIRE_YOUR_CREATED_ROOM,
        listLabel: TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_LABEL,
        listImage: IMAGE_CONSTANTS.V2_RAP_FIRE_FAMILY_ROOM,
      };
    }
    return {
      pageTitle: TEXT_LABELS.RAP_HOUSIE_HOST_PAGE_TITLE,
      backLabel: TEXT_LABELS.RAP_HOUSIE_BACK_TO_GAMES,
      heroTitle: TEXT_LABELS.RAP_HOUSIE_HOST_HERO_TITLE,
      heroSubtitle: TEXT_LABELS.RAP_HOUSIE_HOST_HERO_SUBTITLE,
      inviteLine: TEXT_LABELS.RAP_HOUSIE_HOST_INVITE_LINE,
      heroImage: IMAGE_CONSTANTS.V2_HOUSIE_DASHBOARD_HERO,
      heroBackground: IMAGE_CONSTANTS.V2_REVAMPED_HOUSIE_HERO_BG,
      createLabel: TEXT_LABELS.RAP_HOUSIE_ROOM_CREATE_BUTTON_LABEL,
      sidebarTitle: TEXT_LABELS.RAP_HOUSIE_YOUR_CREATED_ROOM,
      listLabel: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_LABEL,
      listImage: IMAGE_CONSTANTS.V2_HOUSIE_FAMILY_ROOM,
    };
  }, [isRapFire]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        if (isRapFire) {
          const [typesRes, roomsRes] = await Promise.all([
            rapFireSelectRoom().catch(() => null),
            rapFireUserRoom().catch(() => null),
          ]);
          if (!mounted) return;
          const types = typesRes?.data?.data;
          setRoomTypes(Array.isArray(types) && types.length ? types : fallbackRooms);
          const roomsData = roomsRes?.data?.data;
          const rooms = Array.isArray(roomsData)
            ? roomsData
            : roomsData?.room || roomsData?.rooms || [];
          setAvailableRooms(rooms || []);
          setNextGame(roomsData?.nextGame || null);
        } else {
          const [typesRes, roomsRes] = await Promise.all([
            tambolaSelectRoom().catch(() => null),
            tambolaUserRoom().catch(() => null),
          ]);
          if (!mounted) return;
          const types = typesRes?.data?.data;
          setRoomTypes(Array.isArray(types) && types.length ? types : fallbackRooms);
          const roomsData = roomsRes?.data?.data;
          setAvailableRooms(roomsData?.room || []);
          setNextGame(roomsData?.nextGame || null);
        }
      } catch (error) {
        console.error('Failed to load host room screen data:', error);
        if (mounted) setRoomTypes(fallbackRooms);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [isRapFire, fallbackRooms]);

  useEffect(() => {
    if (!location.state?.openCreateRoomModal) return;
    setCreateModalOpen(true);
    setSelectedRoomType(location.state?.pendingRoomType || '');
    setSelectedDisplayRoomData(location.state?.pendingDisplayRoomData || null);
    navigate(location.pathname, {
      replace: true,
      state: {
        isJoin,
        availableRooms: incomingRooms,
      },
    });
  }, [location.pathname, location.state, navigate, isJoin, incomingRooms]);

  const openCreateModal = (room, roomCopy) => {
    logger.log(`${roomCopy.title} create clicked`);
    setSelectedRoomType(room?.heading?.name || roomCopy.title);
    setSelectedDisplayRoomData({
      ...room,
      desc: room?.desc || roomCopy.subtitle,
      descLimit: room?.descLimit || roomCopy.limit,
      descBottom: room?.descBottom || roomCopy.tagline,
    });
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedRoomType('');
    setSelectedDisplayRoomData(null);
  };

  const handleViewRoom = (game, index) => {
    const waitingMediaUrl = resolveWaitingMediaUrl(game);
    if (isRapFire) {
      const contestId =
        game?.contestId || game?.gameId || game?.roomId || game?._id || game?.id || '';
      const roomName = game?.roomName || '';
      navigate(lobbyPath, {
        state: {
          prevScreen: selectRoomPath,
          nextScreen: lobbyPath,
          isJoin: true,
          roomName,
          roomType: game?.roomType,
          nextGame,
          availableRooms,
          index,
          selectedGame: game,
          createdRoomData: {
            contestId,
            gameId: contestId,
            roomName,
            roomType: game?.roomType,
            roomCode: game?.roomCode || game?.code,
            roomImage: waitingMediaUrl || game?.roomImage,
            waitingMediaUrl,
          },
        },
      });
      return;
    }

    const gameIdValue = game?.gameId || '';
    const roomName = game?.roomName || '';
    navigate(lobbyPath, {
      state: {
        prevScreen: selectRoomPath,
        nextScreen: lobbyPath,
        isJoin: true,
        roomName,
        nextGame,
        availableRooms,
        index,
        selectedGame: game,
        createdRoomData: {
          gameId: gameIdValue,
          roomName,
          roomImage: waitingMediaUrl || game?.roomImage,
          waitingMediaUrl,
        },
      },
    });
  };

  const handleCreated = ({
    roomName,
    roomType,
    displayRoomData,
    createdRoomData,
    waitingMedia,
  }) => {
    closeCreateModal();
    if (isRapFire) {
      const contestId =
        createdRoomData?.contestId ||
        createdRoomData?.gameId ||
        createdRoomData?.roomId ||
        createdRoomData?._id ||
        createdRoomData?.id ||
        '';
      navigate(lobbyPath, {
        state: {
          prevScreen: selectRoomPath,
          nextScreen: lobbyPath,
          isJoin: false,
          roomName,
          roomType,
          displayRoomData,
          availableRooms,
          waitingMedia: waitingMedia || null,
          createdRoomData: {
            ...createdRoomData,
            contestId,
            gameId: createdRoomData?.gameId || contestId,
            roomName: createdRoomData?.roomName || roomName,
            roomType: createdRoomData?.roomType || roomType,
          },
        },
      });
      return;
    }
    navigate(lobbyPath, {
      state: {
        prevScreen: selectRoomPath,
        nextScreen: lobbyPath,
        isJoin: isJoin,
        roomName,
        roomType,
        displayRoomData,
        availableRooms,
        createdRoomData,
        waitingMedia: waitingMedia || null,
      },
    });
  };

  const logoSrc =
    IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_RAPCHIK_LOGO ||
    IMAGE_CONSTANTS.V2_REVAMPED_HEADER_LOGO ||
    IMAGE_CONSTANTS.V2_REVAMPED_RAPCHIK_LOGO;

  return (
    <div className="rap-host">
      <header className="rap-host__header">
        <button
          type="button"
          className="rap-host__back"
          onClick={() => navigate(ROUTE_ENUM.SELECT_GAME.routePath)}
        >
          <span className="rap-host__back-icon" aria-hidden="true">
            <img src={IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_BACK_ARROW} alt="" />
          </span>
          <span className="rap-host__back-label">{copy.backLabel}</span>
        </button>
        <h1 className="rap-host__title">{copy.pageTitle}</h1>
        <img className="rap-host__logo" src={logoSrc} alt="Rapchik" />
      </header>

      <div className="rap-host__body">
        <div className="rap-host__main">
          <div className="rap-host__top-banner" data-node-id="20627:2074">
            <section
              className={`rap-host__hero${copy.heroBackground ? ' rap-host__hero--has-bg' : ''}${
                isRapFire ? ' rap-host__hero--rapfire' : ' rap-host__hero--housie'
              }`}
            >
              {copy.heroBackground ? (
                <img
                  className="rap-host__hero-bg"
                  src={copy.heroBackground}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                />
              ) : null}
              {getGameHeroLottie(gameId) ? (
                <Lottie
                  className="rap-host__hero-art rap-host__hero-art--lottie"
                  animationData={getGameHeroLottie(gameId)}
                  loop={!reduceMotion}
                  autoplay={!reduceMotion}
                  renderer="svg"
                  rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
                  aria-label={isRapFire ? 'RapFire' : 'RapHousie'}
                />
              ) : null}
              <h2 className="rap-host__hero-title">{copy.heroTitle}</h2>
              <p className="rap-host__hero-subtitle">{copy.heroSubtitle}</p>
            </section>

            <div className="rap-host__ads">
              <RoomListAdsCarousel />
            </div>
          </div>

          <p className="rap-host__invite">{copy.inviteLine}</p>

          <div className="rap-host__cards">
            {(roomTypes || []).map((room, index) => {
              const roomCopy = resolveRoomCopy(room, fallbacks);
              const imageSrc = isRapFire
                ? getRapFireImageForRoomType(roomCopy.title)
                : getImageForRoomType(roomCopy.title);
              const themeKey = getRoomThemeKey(roomCopy.title);

              return (
                <button
                  key={`${roomCopy.title}-${index}`}
                  type="button"
                  className={`rap-host__card rap-host__card--${themeKey}`}
                  onClick={() => openCreateModal(room, roomCopy)}
                >
                  <div className="rap-host__card-inner">
                    <div className="rap-host__card-art">
                      <img src={imageSrc} alt="" />
                    </div>
                    <div className="rap-host__card-copy">
                      <div className="rap-host__card-heading">
                        <p className="rap-host__card-title">{roomCopy.title}</p>
                        <p className="rap-host__card-subtitle">{roomCopy.subtitle}</p>
                      </div>
                      <p className="rap-host__card-tagline">{roomCopy.tagline}</p>
                    </div>
                    <div className="rap-host__card-actions">
                      {roomCopy.limit ? (
                        <span className="rap-host__limit">{roomCopy.limit}</span>
                      ) : null}
                      <span className="rap-host__create">{copy.createLabel}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="rap-host__sidebar" aria-label={copy.sidebarTitle}>
          <p className="rap-host__sidebar-title">{copy.sidebarTitle}</p>
          <div className="rap-host__sidebar-list">
            {loading ? (
              <div className="rap-host__loading">Loading rooms…</div>
            ) : availableRooms?.length > 0 ? (
              availableRooms.map((game, index) => {
                const roomName = game?.roomName || '';
                const roomTypeLabel = getRoomTypeFromGame(game) || copy.listLabel;
                const themeKey = getRoomThemeKey(roomTypeLabel);
                const roomTypeImage = isRapFire
                  ? getRapFireImageForRoomType(roomTypeLabel) || copy.listImage
                  : getImageForRoomType(roomTypeLabel) || copy.listImage;
                return (
                  <button
                    key={game?.gameId || game?.contestId || index}
                    type="button"
                    className={`rap-host__room-row rap-host__room-row--${themeKey}`}
                    onClick={() => handleViewRoom(game, index)}
                  >
                    <div className="rap-host__room-meta">
                      <div className="rap-host__room-thumb">
                        <img src={roomTypeImage} alt="" />
                      </div>
                      <p className="rap-host__room-name">{roomName}</p>
                    </div>
                    <span className="rap-host__view">
                      {TEXT_LABELS.RAP_HOUSIE_START_GAME_BUTTON_VIEW}
                    </span>
                  </button>
                );
              })
            ) : (
              <EmptyRoom tone="dark" />
            )}
          </div>
        </aside>
      </div>

      <AppFooter />

      <CreateRoomNameModal
        open={createModalOpen}
        onClose={closeCreateModal}
        roomType={selectedRoomType}
        displayRoomData={selectedDisplayRoomData}
        createRoomApi={isRapFire ? rapFireCreateRoom : tambolaCreateRoom}
        roomImage={getHeroImageForRoomType(selectedRoomType)}
        onCreated={handleCreated}
      />
    </div>
  );
};

HostRoomScreen.propTypes = {
  gameId: PropTypes.oneOf(['rap_fire', 'rap_housie']).isRequired,
};

export default HostRoomScreen;
